//PrintUI.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as ExportMap from "./ExportMap";
import * as MainMap from "../MainMap";
import * as PrintMap from "./PrintMap";
import * as PrintUI from "./PrintUI";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import {
   Button, Checkbox, FormControl, FormControlLabel, FormLabel, FormGroup, Icon, IconProps,
   Link, Paper, Radio, TextField, Typography } from "@mui/material";

import React from "react";

const toolName: string = "Print";

export type exportFormatType2D = "pdf" | "png32" | "png8" | "jpg" | "gif" | "eps" | "svg" | "svgz";
type exportFormatType3D = "png" | "jpg";

// To set default format, move it to index zero.
const exportFormats2D = ["JPG - JPEG Compressed Image (small)", "PDF - Adobe PDF (small - export only)", "PNG8 - 256 Color PNG Image (large)", "PNG32 - Full Color PNG Image (very large)", "SVG - Scalable Vector Graphics File", "GIF - 256 Color GIF Image (large)", "EPS - Encapsulated PostScript (export only)"];
const exportFormats3D = ["JPG - JPEG Compressed Image (small)", "PNG - PNG Image (large)"];

export let mapTitle: string;
export let scaleText: string;
export let paperSize: string;
export let portrait: boolean = true;
export let landscape: boolean = false;
export let includeLegend: boolean = false;
export let includeDataTable: boolean = true;
export let printToolActivated: boolean;
export let exportFormat: string;

//Setters.
export function setPrintToolActivated(toolActivated: boolean) {
   printToolActivated = toolActivated;
}

export function getFormat(fullFormat: string) {
   const sa: string[] = fullFormat.split("-");
   return sa[0].trim().toLowerCase();
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of PrintUI.Render");

   //Initialize.
   if (typeof PrintUI.mapTitle === "undefined") {
      mapTitle = Settings.print.title;
      scaleText = Settings.print.scales[0];
      paperSize = Settings.print.paperSizes[0];

      if (MainMap.view2D) {
         exportFormat = exportFormats2D[0];
      } else {
         exportFormat = exportFormats3D[0];
      }
   }

   if (MainMap.view2D) {
      if (!exportFormats2D.includes(exportFormat)) {
         exportFormat = exportFormats2D[0];
      }
   } else {
      if (!exportFormats3D.includes(exportFormat)) {
         exportFormat = exportFormats3D[0];
      }
   }

   //Wire up component for each time render is called, but not on refresh.
   const [initComponent, setState] = React.useState(() => {
      printToolActivated = true;
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
   }, []);

   //Component did update.
   React.useEffect(() => {
      if (DrawerMenu.curToolName === "Print") { // Check for closing transition.
         PrintMap.initPrint();
      }
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Map title.
   function titleChanged(event: React.ChangeEvent<HTMLInputElement>) {
      mapTitle = event.target.value;
      if (PrintUI.mapTitle.trim() === "") {
         mapTitle = Settings.print.title;
      }
   }

   function mapScaleChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      scaleText = event.target.value;
      PrintMap.setZoomToScale(true);
      refresh();
   }

   function paperSizeChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      paperSize = event.target.value;
      refresh();
   }

   function exportFormatChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      exportFormat = event.target.value;
      refresh();
   }

   function orientationChanged(event: React.ChangeEvent<HTMLInputElement>): void {
      const checked: boolean = event.target.checked;
      const value: string = event.target.defaultValue;

      if (value === "Portrait") {
         portrait = checked;
         landscape = !checked;

      } else {
         landscape = checked;
         portrait = !checked;
      }
      refresh();
   }

   function includeChanged(event: React.ChangeEvent<HTMLInputElement>): void {
      const checked: boolean = event.target.checked;
      const value: string = event.target.defaultValue;

      if (value === "Legend") {
         includeLegend = checked;

      } else if (value === "Data Table") {
         includeDataTable = checked;
      }
      refresh();
   }

   //Show map layers menu.
   function showMapLayers() {
      DrawerMenu.showTool("Map Layers");
   }

   //Print button click event.
   function print() {
      if (!PrintMap.busy) {
         PrintMap.getMapURL(); //Print map step 1 of 4.
      }
   }

   //Export map.
   function handleExport() {
      if (!PrintMap.busy) {
         if (MainMap.view2D) { // 2D.
            ExportMap.print(getFormat(exportFormat) as exportFormatType2D);

         } else { // 3D screenshot.
            const options: __esri.SceneViewTakeScreenshotOptions = { width: 800, format: getFormat(exportFormat) as exportFormatType3D };
            MainMap.activeView.takeScreenshot(options).then(function (results) {
               let newWindow = window.open("");
               newWindow.document.title = getFormat("Export " + getFormat(exportFormat));
               const imgElement = document.createElement("img");
               imgElement.src = results.dataUrl;
               newWindow.document.body.appendChild(imgElement);

            }).catch((error) => {
               console.error('Screenshot error:', error);
            });
         }
      }
   }

   let dataTableVisible: boolean = false;
   if (Search.getActiveLayerNumRec() > 0) {
      dataTableVisible = true;
   }

   let legendVisible: boolean = true;
   if (PrintUI.landscape && PrintUI.paperSize.StartsWith("8.5") && PrintUI.includeDataTable && dataTableVisible) {
      includeLegend = false;
      legendVisible = false;
   }

   let exportFormats = exportFormats3D;
   if (MainMap.view2D) {
      exportFormats = exportFormats2D;
   }

   let printDisabled = false;
   let printIconColor: IconProps["color"] = "action";
   if (getFormat(exportFormat) === "pdf" || getFormat(exportFormat) === "eps") {
      printDisabled = true;
      printIconColor = "disabled";
   }

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   const styles: Default.iStyles = {
      formControlLabel: { margin: 0, padding: 0, paddingRight: 15 },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <TextField style={{ marginTop: 7 }}
               id="print-title"
               label="Title"
               defaultValue=""
               margin="dense"
               autoFocus={false}
               fullWidth
               onChange={titleChanged}
            />

            <TextField select style={{ marginRight: 60, marginTop: 15 }}
               label="Map Scale"
               title="Map Scales"
               inputProps={{ id: "map-scales" }}
               InputLabelProps={{ htmlFor: "map-scales" }}
               value={PrintUI.scaleText}
               onChange={mapScaleChanged}
            >
               {Util_MUI.buildSelectMenuItems(Settings.print.scales)}
            </TextField>

            <TextField select style={{ marginTop: 15 }}
               label="Paper Size"
               title="Paper Size"
               inputProps={{ id: "paper-size" }}
               InputLabelProps={{ htmlFor: "paper-size" }}
               value={PrintUI.paperSize}
               onChange={paperSizeChanged}
            >
               {Util_MUI.buildSelectMenuItems(Settings.print.paperSizes)}
            </TextField>

            <FormControl style={{ marginTop: 18 }}>
               <FormLabel htmlFor="portrait-id-prt">Layout</FormLabel>

               <FormGroup row={true}>
                  <FormControlLabel style={styles.formControlLabel}
                     control={<Radio key={Util.uid()} inputProps={{ id: "portrait-id-prt" }} value="Portrait" checked={PrintUI.portrait} onChange={orientationChanged} />}
                     label="Portrait "
                  />
                  <FormControlLabel style={styles.formControlLabel}
                     control={<Radio key={Util.uid()} inputProps={{ id: "landscape-id-prt" }} value="Landscape" checked={PrintUI.landscape} onChange={orientationChanged} />}
                     label="Landscape"
                  />
               </FormGroup>
            </FormControl>

            {(legendVisible || dataTableVisible) &&
               <FormControl style={{ marginTop: 7 }}>

                  <FormLabel htmlFor="legend-id-prt">Include</FormLabel>

                  <FormGroup row={true}>
                     {dataTableVisible && <FormControlLabel style={styles.formControlLabel} title="Show database details"
                        control={<Checkbox value="Data Table" inputProps={{ id: "data-table-id-prt" }} checked={PrintUI.includeDataTable} color="primary" onChange={includeChanged} />}
                        label="Data Table"
                     />}
                     {legendVisible &&
                        <FormControlLabel style={styles.formControlLabel}
                        control={<Checkbox value="Legend" inputProps={{ id: "legend-id-prt" }} checked={PrintUI.includeLegend} color="primary" onChange={includeChanged} />}
                           label="Legend"
                        />}
                  </FormGroup>
               </FormControl>}

            <div className="app-flex-row-around" style={{ marginTop: 7 }}>
               <Button disabled={printDisabled} variant="outlined" color="info" onClick={print} title="Print Map" aria-label="Print Map">
                  <Icon color={printIconColor}>print</Icon>
                  <span style={{ marginLeft: 7 }}></span>
                  Print
               </Button>

               <TextField select style={{ width: 110 }}
                  label="Format"
                  title="Format"
                  inputProps={{ id: "print-format" }}
                  InputLabelProps={{ htmlFor: "print-format" }}
                  value={exportFormat}
                  onChange={exportFormatChanged}
               >
                  {Util_MUI.buildSelectMenuItems(exportFormats)}
               </TextField>

               <Button disabled={(dataTableVisible && includeDataTable) || includeLegend} variant="outlined" color="info" onClick={handleExport} title="Export Map" aria-label="Export Map">
                  Export
               </Button>
            </div>

            {((dataTableVisible && includeDataTable) || includeLegend) && <Typography variant="body2" style={{ margin: 7, marginTop: 22 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To enable exporting the map, exclude the data table and legend.
            </Typography>}

            <Typography variant="body2" style={{ margin: 7, marginTop: 15 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To save the printed map as a PDF, set the printer destination to &quot;<b>Save as PDF</b>&quot; using the Chrome or Edge browser.
            </Typography>

            {includeLegend && <Typography variant="body2" style={{ margin: 7, marginTop: 15 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To set the order of the items in the legend, use the
               <Link component="button" color="textSecondary" onClick={showMapLayers} title="Map Layers" aria-label="Map Layers">
                  &nbsp;<b>Map Layers</b>&nbsp;
               </Link>
               menu and drag layers up or down in the list.
            </Typography>}
         </div>
      </Paper>

   ); //end return.
} //end function.
